// =============================================================
// wagmi.ts — @wagmi/core 기반 지갑 어댑터 (metamask.ts 대체)
//
//  ── 왜 갈아엎었나 ─────────────────────────────────────────
//  기존 metamask.ts 는 @metamask/sdk 를 직접 다루면서
//  "injected 경로 / SDK 경로" 를 수동 분기했다. 이게 모바일에서 깨졌다:
//
//    1) SDK.init() 이 window.ethereum 에 자기 프록시를 주입한다.
//       → 진짜 확장이 없는 모바일 크롬에서도 injected 가 "있는 것처럼" 보임.
//    2) readonlyRPCMap 덕에 eth_chainId 가 지갑 연결 없이도 즉답된다.
//       → isProviderAlive() 가 "살아있음" 으로 오판.
//    3) 결과적으로 SDK 의 정상 connect() 세션 핸드셰이크를 건너뛰고
//       eth_requestAccounts 만 던짐 → MetaMask 앱이 "열리기만" 하고
//       승인 화면 없이 홈만 표시. (실측 로그로 확인)
//
//  DEXignation(사내 프로젝트) 은 wagmi 의 metaMask() 커넥터에 전부 위임하고
//  SDK 옵션을 일절 건드리지 않아 모바일이 안정적으로 동작한다.
//  그 방식을 그대로 이식한다.
//
//  ── 핵심 원칙 ─────────────────────────────────────────────
//   · SDK 라이프사이클(init 타이밍/딥링크/세션복원/프록시 주입)을
//     직접 관리하지 않는다. wagmi 가 검증된 순서로 처리한다.
//   · useDeeplink / communicationLayerPreference / readonlyRPCMap 등
//     SDK 내부 옵션은 넘기지 않는다. dappMetadata 만 준다.
//   · 딥링크 복귀 시 promise 유실은 watchAccount(이벤트 구독)로 잡는다.
//     (폴링이 아니라 provider 가 직접 알려주는 방식)
//
//  ⚠ 외부 계약(WalletAdapter/WalletSnapshot)은 기존과 동일하게 유지한다.
//    main.ts 는 수정 없이 그대로 동작한다.
// =============================================================

import { BrowserProvider } from 'ethers';
import {
  createConfig,
  connect as wagmiConnect,
  disconnect as wagmiDisconnect,
  reconnect as wagmiReconnect,
  getAccount,
  getConnectorClient,
  switchChain,
  watchAccount,
  http,
  createStorage,
  type Config,
  type Connector,
} from '@wagmi/core';
import { metaMask, injected  } from '@wagmi/connectors';
import { defineChain } from 'viem';
import { dlog } from '../debug';
import { CHAIN } from './config';
import { SdkError, type WalletAdapter, type WalletSnapshot } from './types';

// ── 체인 정의 ────────────────────────────────────────────────
// config.ts 의 CHAIN(BSC 메인넷/테스트넷)을 viem chain 으로 변환.
// wagmi 에 단일 체인만 등록하므로, 미연결 사용자가 엉뚱한 체인으로
// 폴백되는 사고가 원천 차단된다. (DEXignation 과 동일 전략)
const activeChain = defineChain({
  id: CHAIN.id,
  name: CHAIN.name,
  nativeCurrency: CHAIN.nativeCurrency,
  rpcUrls: { default: { http: [CHAIN.rpc] } },
  blockExplorers: { default: { name: 'Explorer', url: CHAIN.explorer } },
  testnet: CHAIN.id === 97,
});

// dapp 메타데이터 — MetaMask 앱 연결 화면에 표시된다.
// url 은 실제 서비스 도메인과 일치해야 한다(불일치 시 앱이 요청을 거를 수 있음).
const SITE_URL =
  typeof window !== 'undefined' ? window.location.origin : 'https://molebang.com';

export class WagmiWalletAdapter implements WalletAdapter {
  private config: Config;
  private address: string | null = null;
  private chainOk = false;
  private listeners = new Set<(s: WalletSnapshot) => void>();
  private unwatch: (() => void) | null = null;

  // 딥링크 복귀 시 connect() 의 promise 가 유실될 수 있다.
  // watchAccount 가 계정을 먼저 잡으면 이 resolver 로 대기 중인 connect() 를 깨운다.
  private pendingResolve: ((addr: string) => void) | null = null;

  constructor() {
    this.config = createConfig({
      chains: [activeChain],
      connectors: [
        // [0] PC 브라우저 확장 (EIP-6963). MetaMask/Rabby/Brave 등.
        injected({ shimDisconnect: true }),
        // [1] 모바일 딥링크 (@metamask/sdk 기반).
        metaMask({
          dappMetadata: {
            name: 'MoleBang',
            url: SITE_URL,
            iconUrl: `${SITE_URL}/favicon.ico`,
          },
        }),
      ],
      // 새로고침/앱 복귀 후에도 연결이 유지되도록 localStorage 에 영속.
      storage: createStorage({
        storage: typeof window !== 'undefined' ? window.localStorage : undefined,
        key: 'molebang.wagmi',
      }),
      transports: { [activeChain.id]: http(CHAIN.rpc) },
    });

    this.startWatching();
  }

  // ── 계정 변화 구독 ──────────────────────────────────────
  //  딥링크 왕복의 race condition 을 여기서 흡수한다.
  //  MetaMask 앱에서 승인하고 브라우저로 돌아오면, wagmi 가 세션을 복원하며
  //  이 콜백이 먼저 불릴 수 있다. connect() 가 아직 대기 중이면 깨워준다.
  private startWatching(): void {
    this.unwatch = watchAccount(this.config, {
      onChange: (account) => {
        const addr = account.address ? account.address.toLowerCase() : null;
        const changed = addr !== this.address;

        this.address = addr;
        this.chainOk = account.chainId === activeChain.id;

        if (changed) {
          dlog(
            `watchAccount: status=${account.status} addr=${addr ?? 'null'} chain=${account.chainId ?? 'null'}`,
          );
        }

        // 대기 중인 connect() 를 깨운다 (딥링크 복귀 경로).
        if (addr && this.pendingResolve) {
          dlog('watchAccount 가 계정 감지 → 대기 중인 connect() 해제');
          const resolve = this.pendingResolve;
          this.pendingResolve = null;
          resolve(addr);
        }

        if (changed) this.emit();
      },
    });
  }

  /**
   * 환경에 맞는 커넥터를 고른다.
   *  · PC + 확장 있음 → injected (팝업 즉시, 딥링크 불필요)
   *  · 그 외(모바일)  → metaMask SDK (딥링크)
   * 확장이 없는 PC 에서 injected 를 쓰면 ProviderNotFoundError 가 나므로
   * window.ethereum 존재 여부로 판단한다.
   */
  private get connector(): Connector {
    const hasInjected =
      typeof window !== 'undefined' &&
      !!(window as { ethereum?: unknown }).ethereum;
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    const pick = !isMobile && hasInjected
      ? this.config.connectors.find((c) => c.id === 'injected')
      : this.config.connectors.find((c) => c.id === 'metaMaskSDK');

    const c = pick ?? this.config.connectors[0];
    if (!c) throw new SdkError('NO_WALLET', 'MetaMask 커넥터를 찾을 수 없습니다.');
    dlog(`커넥터 선택: ${c.id} (mobile=${isMobile} injected=${hasInjected})`);
    return c;
  }

  // ── 연결 ────────────────────────────────────────────────
  async connect(): Promise<string> {
    if (this.address) return this.address;

    dlog(
      `connect() 진입 — mobile=${/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)} ` +
        `injected=${!!(window as { ethereum?: unknown }).ethereum}`,
    );

    // 딥링크 복귀 시 promise 가 유실되어도 watchAccount 가 계정을 잡으면
    // 이 promise 가 resolve 된다. 둘 중 먼저 오는 쪽을 채택.
    const viaWatch = new Promise<string>((resolve) => {
      this.pendingResolve = resolve;
    });

    try {
      dlog('wagmi connect() 호출 (딥링크 발동 지점)');
      const viaConnect = wagmiConnect(this.config, {
        connector: this.connector,
      }).then((r) => {
        const a = r.accounts?.[0];
        if (!a) throw new SdkError('USER_REJECTED', '연결할 계정이 없습니다.');
        dlog('wagmi connect() 응답: ' + a);
        return a.toLowerCase();
      });

      const addr = await Promise.race([viaConnect, viaWatch]);

      this.address = addr;
      dlog('✅ 연결 성공: ' + addr);

      await this.ensureChain().catch((e) =>
        dlog('ensureChain 실패(무시): ' + String(e)),
      );
      this.emit();
      return addr;
    } catch (e) {
      const err = e as { code?: number; name?: string; message?: string };
      const msg = String(err.message ?? e);
      dlog(`❌ connect 실패: code=${err.code} name=${err.name} msg=${msg}`);

      // 실패했더라도 watchAccount 가 이미 계정을 잡았을 수 있다(복귀 경로).
      const acc = getAccount(this.config);
      if (acc.address) {
        this.address = acc.address.toLowerCase();
        dlog('✅ 실패 처리 중 계정 발견: ' + this.address);
        this.emit();
        return this.address;
      }

      if (err.name === 'ProviderNotFoundError') {
        throw new SdkError('NO_WALLET', 'MetaMask 를 찾을 수 없습니다.');
      }
      if (err.code === 4001 || /reject|denied|cancel/i.test(msg)) {
        throw new SdkError('USER_REJECTED', '연결을 취소했습니다.');
      }
      throw new SdkError('NO_WALLET', 'MetaMask 연결에 실패했습니다.');
    } finally {
      this.pendingResolve = null;
    }
  }

  // ── 세션 복원 ───────────────────────────────────────────
  //  wagmi 의 reconnect 가 storage 에 남은 세션을 되살린다.
  //  실패해도 조용히 넘어간다(첫 방문이면 세션이 없는 게 정상).
  async restore(): Promise<void> {
    try {
      dlog('restore(): wagmi reconnect 시도');
      await wagmiReconnect(this.config, { connectors: [this.connector] });
      const acc = getAccount(this.config);
      if (acc.address) {
        this.address = acc.address.toLowerCase();
        this.chainOk = acc.chainId === activeChain.id;
        dlog('restore(): 세션 복원됨 → ' + this.address);
        this.emit();
      } else {
        dlog('restore(): 복원할 세션 없음');
      }
    } catch (e) {
      dlog('restore() 실패(무시): ' + String(e));
    }
  }

  async disconnect(): Promise<void> {
    try {
      await wagmiDisconnect(this.config);
    } catch (e) {
      dlog('disconnect 실패(무시): ' + String(e));
    }
    this.address = null;
    this.chainOk = false;
    this.emit();
  }

  // ── ethers Provider ─────────────────────────────────────
  //  기존 코드가 ethers BrowserProvider 를 쓰므로, wagmi 의 EIP-1193
  //  클라이언트를 감싸서 동일한 타입으로 돌려준다.
  async getProvider(): Promise<BrowserProvider> {
    if (!this.address) {
      throw new SdkError('NO_WALLET', '지갑이 연결되지 않았습니다.');
    }
    // wagmi 공식 ethers 어댑터 패턴.
    //  · BrowserProvider 는 (EIP-1193 transport, network) 두 인자를 받는다.
    //  · network 를 생략하면 ethers 가 별도 조회를 하며 모바일에서 지연이 생긴다.
    const client = await getConnectorClient(this.config);
    const network = {
      chainId: client.chain.id,
      name: client.chain.name,
    };
    return new BrowserProvider(client.transport as never, network);
  }

  // ── 체인 전환 ───────────────────────────────────────────
  async ensureChain(): Promise<void> {
    const acc = getAccount(this.config);
    if (acc.chainId === activeChain.id) {
      this.chainOk = true;
      return;
    }
    try {
      dlog(`ensureChain: ${acc.chainId} → ${activeChain.id} 전환 시도`);
      await switchChain(this.config, { chainId: activeChain.id });
      this.chainOk = true;
      dlog('ensureChain: 전환 성공');
    } catch (e) {
      const err = e as { code?: number; message?: string };
      this.chainOk = false;
      dlog(`ensureChain 실패: code=${err.code} msg=${err.message}`);
      if (err.code === 4001) {
        throw new SdkError('USER_REJECTED', '체인 전환을 취소했습니다.');
      }
      throw new SdkError('NO_WALLET', `${CHAIN.name} 으로 전환해주세요.`);
    }
    this.emit();
  }

  /** 현재 연결된 주소 (미연결 시 null) */
  getAddress(): string | null {
    return this.address;
  }

  // ── 구독/스냅샷 (기존과 동일한 계약) ────────────────────
  onChange(cb: (s: WalletSnapshot) => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  snapshot(): WalletSnapshot {
    return {
      connected: !!this.address,
      address: this.address,
      short: this.address
        ? `${this.address.slice(0, 6)}…${this.address.slice(-4)}`
        : null,
      chainOk: this.chainOk,
    };
  }

  private emit(): void {
    const snap = this.snapshot();
    for (const cb of this.listeners) cb(snap);
  }

  /** 페이지 언로드 시 구독 해제 (선택적 호출) */
  destroy(): void {
    this.unwatch?.();
    this.unwatch = null;
    this.listeners.clear();
  }
}